import {
  DEFAULT_PROJECT_ID,
  DEFAULT_PROJECT_ID_SLUG,
} from '@unifyapps/network/utils/consts/project';
import { addTenantPrefix } from '@unifyapps/network/utils/tenantBasePath';
import { SOURCE_CONTEXT_KEY, SOURCE_CONTEXT_PAYLOAD } from '../../consts/navigation';
import { isPlatformMatrixUAPage } from '../../hooks/useLink';
import { getWindow } from '../../utils/browser';
import type { ProcessURLMiddleware } from '../useRouter';

export const PROJECT_DETAILS_PREFIX = '/p';
const ENABLED_PROJECT_ROUTES = [
  '/automations',
  '/automation-interfaces',
  '/api-platform',
  '/pipelines',
  '/interfaces',
  '/object',
  '/objects',
  '/campaigns',
  '/segments',
  '/connections',
  '/templates',
  '/env-variables',
  '/event-streams',
  '/decision-tables',
  '/code-snippets',
  '/prompts',
  '/ai-teams',
  '/ai-agents',
  '/ai-deployments',
  '/skills',
  '/skill-groups',
  '/data-quality',
  '/ontology',
  '/copilot',
  '/enterprise-search',
  '/es-settings',
  '/observability',
  '/knowledge-set',
  '/memory-set',
  '/data-catalog',
  '/evaluation',
  '/ai-setup',
  '/model-library',
  '/models-library',
  '/mcp-servers',
  '/ai-metering',
  '/a2a-servers',
  '/design-system',
  '/context-graphs',
  '/agent-governance',
];

export const getAdaptedProjectSlug = (
  projectSlug?: string | undefined | null | number,
  projectId?: number,
) => {
  let adaptedProjectSlug = projectSlug;

  if (projectSlug === DEFAULT_PROJECT_ID_SLUG && projectId) {
    adaptedProjectSlug = `0${String(projectId)}`; // we have to prefix projectId with 0 if twe are currently outside any project, but opening an entity mapped to a project
  }

  return adaptedProjectSlug;
};

export const getAdaptedProjectsRoute = (
  route: string,
  projectSlug?: string | undefined | null | number,
) => {
  if (route.startsWith(`${PROJECT_DETAILS_PREFIX}/`)) {
    return route;
  }

  const shouldPrefix = ENABLED_PROJECT_ROUTES.some((r) => route.startsWith(r));

  if (!shouldPrefix) {
    return route;
  }

  return `${PROJECT_DETAILS_PREFIX}/${String(projectSlug || DEFAULT_PROJECT_ID)}${route}`;
};

export const getAdaptedProjectsRouteMiddleware: ProcessURLMiddleware = (
  route,
  options,
  context,
) => {
  const projectSlug = getAdaptedProjectSlug(context?.projectSlug, options?.pId);

  return getAdaptedProjectsRoute(route, projectSlug);
};

export const getRouteWithSourceInfoMiddleware: ProcessURLMiddleware = (route: string, options) => {
  const { source, sourcePayload } = options || {};
  if (source) {
    const url = new URL(route, getWindow()?.location.origin);
    url.searchParams.set(SOURCE_CONTEXT_KEY, source);

    if (sourcePayload) {
      url.searchParams.set(SOURCE_CONTEXT_PAYLOAD, JSON.stringify(sourcePayload));
    }

    return url.toString();
  }

  return route;
};

export const getUrlWithBasePath: ProcessURLMiddleware = (route: string, options, context) => {
  const basePath = context?.queryMeta?.basePath;
  if (!basePath) {
    return route;
  }

  return addTenantPrefix(route, basePath);
};

/** For matrix platform pages, we need to ensure that /ua/ is prefixed to the route if not already present
 * (e.g. when navigating from /ua/ai-agents to /ai-agents/123, we need to ensure that /ua/ai-agents/123 is the final route)
 */
export const getUrlWithUAPath: ProcessURLMiddleware = (route: string, options, context) => {
  // add /ua/ prefix if not already present in the current path or in the route
  if (isPlatformMatrixUAPage() && !route.startsWith('/ua/')) {
    return `/ua${route}`;
  }

  return route;
};
